// File: e1c/src/components/content_editor/MediaLibraryModal.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Grid, Card,
    CardMedia, CardActionArea, Typography, TextField, CircularProgress, Alert, Checkbox,
    FormControlLabel, Pagination
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideocamIcon from '@mui/icons-material/Videocam';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import authFetch from '../../utils/authFetch';

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`media-tabpanel-${index}`} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const MediaLibraryModal = ({ open, onClose, onSelectMedia }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [libraryAssets, setLibraryAssets] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedAssets, setSelectedAssets] = useState({}); // Use an object for quick lookup

    const [uploadFile, setUploadFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    // FIX: Get the API base URL to prepend to relative thumbnail paths
    const apiUrlBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    const fetchLibrary = useCallback((pageNum = 1) => {
        setIsLoading(true);
        setError('');
        authFetch(`/api/v1/media/media-library?page=${pageNum}&limit=12`)
            .then(data => {
                setLibraryAssets(data.assets);
                setPagination(data.pagination);
            })
            .catch(err => setError(`Failed to load library: ${err.message}`))
            .finally(() => setIsLoading(false));
    }, []);

    // Load library when tab becomes visible
    useEffect(() => {
        if (open && tabIndex === 0) {
            fetchLibrary(page);
        }
    }, [open, tabIndex, page, fetchLibrary]);

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
        setError('');
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    const handleToggleSelectAsset = (asset) => {
        setSelectedAssets(prev => {
            const newSelected = { ...prev };
            if (newSelected[asset.id]) {
                delete newSelected[asset.id];
            } else {
                newSelected[asset.id] = asset;
            }
            return newSelected;
        });
    };

    const handleConfirmSelection = () => {
        onSelectMedia(Object.values(selectedAssets));
        handleCloseModal();
    };

    const handleCloseModal = () => {
        onClose();
        // Reset state for next time
        setTimeout(() => {
            setTabIndex(0);
            setSelectedAssets({});
            setError('');
            setUploadFile(null);
            setVideoUrl('');
        }, 300);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!["image/jpeg", "image/png", "image/heic", "image/heif"].includes(file.type)) {
                setError("File type not supported. Please upload JPEG, PNG, or HEIC.");
                return;
            }
            if (file.size > (10 * 1024 * 1024)) { // 10MB limit
                setError("File exceeds 10MB limit.");
                return;
            }
            setUploadFile(file);
            setError('');
        }
    };

    const handleUploadImage = async () => {
        if (!uploadFile) return;
        setIsUploading(true);
        setError('');

        const formData = new FormData();
        formData.append("file", uploadFile);

        try {
            await authFetch('/api/v1/media/upload-image', {
                method: 'POST',
                body: formData,
            });
            // Success! Go back to the library tab, which will auto-refresh
            setUploadFile(null);
            setTabIndex(0);
            fetchLibrary(1); // Refresh library
        } catch (err) {
            setError(`Upload Failed: ${err.message}`);
        } finally {
            setIsUploading(false);
        }
    };
    
    const handleRegisterVideo = async () => {
        if (!videoUrl) return;
        setIsRegistering(true);
        setError('');
        
        try {
            await authFetch('/api/v1/media/register-video', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ source_url: videoUrl }),
            });
            // Success! Go back to library
            setVideoUrl('');
            setTabIndex(0);
            fetchLibrary(1); // Refresh library
        } catch (err) {
            setError(`Registration Failed: ${err.message}`);
        } finally {
            setIsRegistering(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="md">
            <DialogTitle>Media Library</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth">
                    <Tab label="Select from Library" icon={<AddPhotoAlternateIcon />} iconPosition="start" />
                    <Tab label="Upload New Image" icon={<UploadFileIcon />} iconPosition="start" />
                    <Tab label="Register Video URL" icon={<VideocamIcon />} iconPosition="start" />
                </Tabs>
                {error && <Alert severity="error" sx={{ m: 2, mb: 0 }}>{error}</Alert>}

                <TabPanel value={tabIndex} index={0}>
                    {isLoading && <CircularProgress />}
                    {!isLoading && libraryAssets.length === 0 && <Typography>No media found.</Typography>}
                    <Grid container spacing={2}>
                        {libraryAssets.map(asset => {
                            const isSelected = !!selectedAssets[asset.id];
                            return (
                                <Grid item xs={6} sm={4} md={3} key={asset.id}>
                                    <Card variant="outlined" sx={{ position: 'relative', opacity: isSelected ? 0.7 : 1 }}>
                                        <CardActionArea onClick={() => handleToggleSelectAsset(asset)}>
                                            
                                        {/* FIX: Use apiUrlBase for relative thumbnail URLs */}
                                        {asset.thumbnail_url ? (
                                            <CardMedia
                                                component="img"
                                                image={asset.thumbnail_url?.startsWith('http') 
                                                    ? asset.thumbnail_url 
                                                    : `${apiUrlBase}${asset.thumbnail_url}`}        
                                                alt={asset.original_filename || 'Media Asset'}
                                                sx={{ height: 140, objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'grey.200' }}>
                                                {asset.media_type === 'video' ? <VideocamIcon color="action" /> : <ImageIcon color="action" />}
                                            </Box>
                                        )}

                                            <Typography variant="caption" display="block" noWrap sx={{ p: 1, textAlign: 'center' }}>
                                                {asset.original_filename || asset.url}
                                            </Typography>
                                        </CardActionArea>
                                        {isSelected && (
                                            <CheckCircleIcon 
                                                color="success" 
                                                sx={{ position: 'absolute', top: 8, right: 8, fontSize: 30, backgroundColor: 'white', borderRadius: '50%' }} 
                                            />
                                        )}
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                    {pagination && pagination.total_pages > 1 && (
                        <Pagination 
                            count={pagination.total_pages} 
                            page={page} 
                            onChange={handlePageChange} 
                            sx={{ mt: 3, display: 'flex', justifyContent: 'center' }} 
                        />
                    )}
                </TabPanel>

                <TabPanel value={tabIndex} index={1}>
                    <Typography gutterBottom>Upload a new image (JPEG, PNG, HEIC) up to 10MB.</Typography>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                    >
                        Select Image File
                        <input type="file" hidden accept="image/jpeg,image/png,image/heic,image/heif" onChange={handleFileChange} />
                    </Button>
                    {uploadFile && <Typography sx={{ display: 'block', mt: 1 }}>Selected: {uploadFile.name}</Typography>}
                    <Button
                        variant="contained"
                        onClick={handleUploadImage}
                        disabled={!uploadFile || isUploading}
                        sx={{ display: 'block', mt: 2 }}
                    >
                        {isUploading ? <CircularProgress size={24} /> : 'Upload and Process Image'}
                    </Button>
                </TabPanel>
                
                <TabPanel value={tabIndex} index={2}>
                    <Typography gutterBottom>Add a video from YouTube or Vimeo to your library.</Typography>
                    <TextField
                        fullWidth
                        label="YouTube or Vimeo URL"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        variant="outlined"
                        size="small"
                        placeholder="https://www.youtube.com/watch?v=..."
                    />
                    <Button
                        variant="contained"
                        onClick={handleRegisterVideo}
                        disabled={!videoUrl || isRegistering}
                        sx={{ mt: 2 }}
                    >
                        {isRegistering ? <CircularProgress size={24} /> : 'Register Video'}
                    </Button>
                </TabPanel>

            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseModal}>Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleConfirmSelection}
                    disabled={Object.keys(selectedAssets).length === 0}
                >
                    Attach Selected ({Object.keys(selectedAssets).length})
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MediaLibraryModal;